import React, { useState, useEffect } from 'react';
import { ArrowLeftRight, History, Calculator, Ruler, Copy, Check } from 'lucide-react';

interface HistoryEntry {
  id: number;
  from: string;
  to: string;
  timestamp: string;
}

interface UnitDefinition {
  factor: number;
  category: string;
  symbol: string;
}

type UnitName = 'nanometers' | 'micrometers' | 'millimeters' | 'centimeters' | 'decimeters' | 'meters' | 'dekameters' | 'hectometers' | 'kilometers' | 'inches' | 'feet' | 'yards' | 'miles' | 'thou' | 'nautical miles' | 'fathoms' | 'astronomical units' | 'light years' | 'parsecs' | 'cubits' | 'rods' | 'chains' | 'furlongs';

const LengthConverter: React.FC = () => {
  const [fromValue, setFromValue] = useState<string>('');
  const [toValue, setToValue] = useState<string>('');
  const [fromUnit, setFromUnit] = useState<UnitName>('meters');
  const [toUnit, setToUnit] = useState<UnitName>('feet');
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [precision, setPrecision] = useState<number>(4);

  // Comprehensive unit definitions with conversion factors to meters
  const units: Record<UnitName, UnitDefinition> = {
    // Metric
    'nanometers': { factor: 1e-9, category: 'Metric', symbol: 'nm' },
    'micrometers': { factor: 1e-6, category: 'Metric', symbol: 'μm' },
    'millimeters': { factor: 0.001, category: 'Metric', symbol: 'mm' },
    'centimeters': { factor: 0.01, category: 'Metric', symbol: 'cm' },
    'decimeters': { factor: 0.1, category: 'Metric', symbol: 'dm' },
    'meters': { factor: 1, category: 'Metric', symbol: 'm' },
    'dekameters': { factor: 10, category: 'Metric', symbol: 'dam' },
    'hectometers': { factor: 100, category: 'Metric', symbol: 'hm' },
    'kilometers': { factor: 1000, category: 'Metric', symbol: 'km' },
    
    // Imperial/US
    'inches': { factor: 0.0254, category: 'Imperial', symbol: 'in' },
    'feet': { factor: 0.3048, category: 'Imperial', symbol: 'ft' },
    'yards': { factor: 0.9144, category: 'Imperial', symbol: 'yd' },
    'miles': { factor: 1609.344, category: 'Imperial', symbol: 'mi' },
    'thou': { factor: 0.0000254, category: 'Imperial', symbol: 'th' },
    
    // Nautical
    'nautical miles': { factor: 1852, category: 'Nautical', symbol: 'nmi' },
    'fathoms': { factor: 1.8288, category: 'Nautical', symbol: 'ftm' },
    
    // Astronomical
    'astronomical units': { factor: 149597870700, category: 'Astronomical', symbol: 'AU' },
    'light years': { factor: 9.461e15, category: 'Astronomical', symbol: 'ly' },
    'parsecs': { factor: 3.086e16, category: 'Astronomical', symbol: 'pc' },
    
    // Ancient/Historical
    'cubits': { factor: 0.4572, category: 'Historical', symbol: 'cbt' },
    'rods': { factor: 5.0292, category: 'Historical', symbol: 'rd' },
    'chains': { factor: 20.1168, category: 'Historical', symbol: 'ch' },
    'furlongs': { factor: 201.168, category: 'Historical', symbol: 'fur' }
  };

  const categories = Array.from(new Set(Object.values(units).map(unit => unit.category)));

  const convert = (value: number, from: UnitName, to: UnitName): number => {
    if (!value || isNaN(value)) return 0;
    const fromFactor = units[from].factor;
    const toFactor = units[to].factor;
    const result = (value * fromFactor) / toFactor;
    return parseFloat(result.toPrecision(precision));
  };

  const handleFromValueChange = (value: string): void => {
    setFromValue(value);
    if (value && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), fromUnit, toUnit);
      setToValue(converted.toString());
    } else {
      setToValue('');
    }
  };

  const handleToValueChange = (value: string): void => {
    setToValue(value);
    if (value && !isNaN(Number(value))) {
      const converted = convert(parseFloat(value), toUnit, fromUnit);
      setFromValue(converted.toString());
    } else {
      setFromValue('');
    }
  };

  const swapUnits = (): void => {
    const tempUnit = fromUnit;
    const tempValue = fromValue;
    
    setFromUnit(toUnit);
    setToUnit(tempUnit);
    setFromValue(toValue);
    setToValue(tempValue);
  };

  const addToHistory = (): void => {
    if (fromValue && toValue) {
      const entry: HistoryEntry = {
        id: Date.now(),
        from: `${fromValue} ${units[fromUnit].symbol}`,
        to: `${toValue} ${units[toUnit].symbol}`,
        timestamp: new Date().toLocaleTimeString()
      };
      setHistory(prev => [entry, ...prev.slice(0, 9)]); // Keep last 10 entries
    }
  };

  const copyResult = (): void => {
    if (toValue) {
      navigator.clipboard.writeText(`${toValue} ${units[toUnit].symbol}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const clearAll = (): void => {
    setFromValue('');
    setToValue('');
  };

  useEffect(() => {
    if (fromValue && !isNaN(Number(fromValue))) {
      const converted = convert(parseFloat(fromValue), fromUnit, toUnit);
      setToValue(converted.toString());
    }
  }, [fromUnit, toUnit, precision, fromValue]);

  const renderUnitOptions = (): React.ReactElement[] => {
    return categories.map(category => (
      <optgroup key={category} label={category}>
        {Object.entries(units)
          .filter(([_, unit]) => unit.category === category)
          .map(([unitName, unit]) => (
            <option key={unitName} value={unitName}>
              {unitName} ({unit.symbol})
            </option>
          ))}
      </optgroup>
    ));
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-blue-50 to-indigo-100 min-h-screen">
      <div className="bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <Ruler className="w-8 h-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-800">Advanced Length Converter</h1>
          </div>
          <p className="text-gray-600">Convert between multiple length units with precision</p>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-4 mb-6 justify-center">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Precision:</label>
            <select 
              value={precision} 
              onChange={(e) => setPrecision(parseInt(e.target.value))}
              className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {[2, 3, 4, 5, 6, 8, 10].map(p => (
                <option key={p} value={p}>{p} digits</option>
              ))}
            </select>
          </div>
          
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <History className="w-4 h-4" />
            History
          </button>
          
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
          >
            Clear All
          </button>
        </div>

        {/* Main Converter */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* From Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              From
            </h3>
            <div className="space-y-3">
              <input
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                placeholder="Enter value"
                className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
            </div>
          </div>

          {/* To Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              To
            </h3>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type="number"
                  value={toValue}
                  onChange={(e) => handleToValueChange(e.target.value)}
                  placeholder="Result"
                  className="w-full px-4 py-3 text-lg border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
                />
                <button
                  onClick={copyResult}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Copy result"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4 text-gray-500" />}
                </button>
              </div>
              <select
                value={toUnit}
                onChange={(e) => setToUnit(e.target.value as UnitName)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {renderUnitOptions()}
              </select>
            </div>
          </div>
        </div>

        {/* Swap Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={swapUnits}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-md"
          >
            <ArrowLeftRight className="w-5 h-5" />
            Swap Units
          </button>
        </div>

        {/* Add to History Button */}
        {fromValue && toValue && (
          <div className="flex justify-center mb-6">
            <button
              onClick={addToHistory}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
            >
              Add to History
            </button>
          </div>
        )}

        {/* Conversion Summary */}
        {fromValue && toValue && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-800 mb-2">Conversion Summary</h4>
            <p className="text-lg">
              <span className="font-mono bg-white px-2 py-1 rounded">{fromValue} {units[fromUnit].symbol}</span>
              <span className="mx-3 text-gray-500">=</span>
              <span className="font-mono bg-white px-2 py-1 rounded font-semibold text-blue-700">{toValue} {units[toUnit].symbol}</span>
            </p>
          </div>
        )}

        {/* History */}
        {showHistory && (
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <History className="w-5 h-5" />
              Conversion History
            </h4>
            {history.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No conversions in history</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-center bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm">{entry.from}</span>
                      <ArrowLeftRight className="w-4 h-4 text-gray-400" />
                      <span className="font-mono text-sm font-semibold">{entry.to}</span>
                    </div>
                    <span className="text-xs text-gray-500">{entry.timestamp}</span>
                  </div>
                ))}
              </div>
            )}
            {history.length > 0 && (
              <button
                onClick={() => setHistory([])}
                className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm"
              >
                Clear History
              </button>
            )}
          </div>
        )}

        {/* Unit Categories Info */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map(category => (
            <div key={category} className="bg-gray-50 rounded-lg p-4">
              <h5 className="font-semibold text-gray-800 mb-2">{category}</h5>
              <div className="space-y-1">
                {Object.entries(units)
                  .filter(([_, unit]) => unit.category === category)
                  .slice(0, 4)
                  .map(([unitName, unit]) => (
                    <div key={unitName} className="text-sm text-gray-600">
                      {unit.symbol} - {unitName}
                    </div>
                  ))}
                {Object.entries(units).filter(([_, unit]) => unit.category === category).length > 4 && (
                  <div className="text-xs text-gray-500">+ more...</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LengthConverter;